// Shared helpers for computing default values and validating parameters
// described by the backend's `to_json_schema()` output (Parameter.jsx /
// LineshapeParameterModal.jsx). Kept in one place so every schema type,
// including nested ones (objects, discriminated unions), is handled the
// same way everywhere a schema is rendered.

// Build the default value for a discriminated_union field: an object
// tagged with the discriminator key plus defaults for the selected
// option's own parameters, e.g. { kind: "barrier_factor", q0: 0.15 }.
export function getDiscriminatedUnionDefault(param, initialValue) {
    const discriminator = param.discriminator || 'kind';
    const kind = initialValue?.[discriminator] !== undefined
        ? initialValue[discriminator]
        : param.default;
    const option = param.options?.[kind] || {};
    const result = { [discriminator]: kind };
    if (option.parameters) {
        Object.entries(option.parameters).forEach(([subKey, subParam]) => {
            result[subKey] = getDefaultParamValue(subParam, initialValue?.[subKey]);
        });
    }
    return result;
}

export function getDefaultParamValue(param, initialValue) {
    if (initialValue !== undefined) {
        return initialValue;
    }
    if (param.type === 'discriminated_union') {
        return getDiscriminatedUnionDefault(param, initialValue);
    }
    if (param.default !== undefined && param.default !== "PydanticUndefined") {
        return param.default.value !== undefined ? param.default.value : param.default;
    }
    if (param.optional === true) {
        return null;
    }
    if (param.type === 'boolean') {
        return false;
    }
    if (param.type === 'number' || param.type === 'integer') {
        return 0;
    }
    if (param.type === 'array') {
        return [];
    }
    if (param.type === 'object') {
        return {};
    }
    return '';
}

// Compute default values for every entry of a schema dict (e.g.
// fixed_parameters, optimization_parameters, or a nested object schema),
// honoring any already-known values.
export function getDefaultValuesForSchema(schemaDict, initialValues) {
    const result = {};
    if (!schemaDict) return result;
    Object.entries(schemaDict).forEach(([key, param]) => {
        result[key] = getDefaultParamValue(param, initialValues?.[key]);
    });
    return result;
}

// Validate a single parameter value against its schema, recursing into
// the currently-selected option of discriminated_union fields.
export function validateParam(key, param, value, path = key) {
    if (param.type === 'discriminated_union') {
        const discriminator = param.discriminator || 'kind';
        const kind = value?.[discriminator] !== undefined ? value[discriminator] : param.default;
        const option = param.options?.[kind];
        if (!option) {
            return [`Parameter '${path}' has an unknown ${discriminator} '${kind}'`];
        }
        const errors = [];
        Object.entries(option.parameters || {}).forEach(([subKey, subParam]) => {
            errors.push(...validateParam(subKey, subParam, value?.[subKey], `${path}.${subKey}`));
        });
        return errors;
    }

    if (param.optional !== true) {
        if (value === null || value === undefined || value === '') {
            return [`Parameter '${path}' is required but not set`];
        }
    }
    return [];
}
